// # 타임라인 캐시 — 채널별 메시지 본문이 사는 곳
//
// ## 왜 Query 캐시가 아닌가
// 소켓 사건은 "42번 줄이 생겼다" 까지만 말한다. 본문은 REST 로 받아야 하는데,
// 그때마다 최근 50줄을 통째로 다시 긁으면 **줄 하나에 50줄어치 대역폭**이다.
// 바쁜 채널에서는 초당 몇 번씩 그런 일이 일어난다.
//
// 여기서는 채널마다 "여기까지 가졌다"(`have`) 를 들고, 사건이 오면 `have‥=last`
// 구간만 한 번 묻는다. 사건이 몰려 와도 **채널당 한 요청**만 나간다 — 채우는 동안
// 온 사건은 목표만 끌어올리고, 끝나면 다시 그 목표까지 잇는다.
//
// ## 저장소(`store.ts`)와 나누는 선
// 저장소는 뱃지·커서(모든 채널, 항상). 여기는 본문(연 채널만, 열 때부터).
// 안 연 채널의 사건은 `have` 도 건드리지 않는다 — 열 때 최근 묶음부터 받으면 된다.

import type { ChannelId, Event, Seq } from '@maru/protocol'
import { has_channel } from '@maru/protocol'
import type { MaruApi, Message } from './api.ts'

/** 화면이 보는 채널 하나. **같은 상태면 같은 객체**다(`useSyncExternalStore` 규약). */
export interface Slice {
  lines: readonly Message[]
  /** 더 오래된 줄이 없다 — "더 보기" 를 그리지 않는다. */
  reached_start: boolean
  /** 첫 묶음 또는 과거 묶음을 기다리는 중. */
  loading: boolean
}

const EMPTY: Slice = { lines: [], reached_start: false, loading: true }

/** `reach` 가 거슬러 올라갈 페이지 한도. 50 줄 × 20 = 천 줄이면 충분하다. */
const MAX_REACH = 20

interface Held {
  by_seq: Map<Seq, Message>
  /** 이 seq 까지는 빠짐없이 가졌다. */
  have: Seq
  /** 사건이 말한 가장 큰 seq. `have` 보다 크면 채울 것이 있다. */
  want: Seq
  reached_start: boolean
  loading: boolean
  filling: Promise<void> | null
  /** 수정·삭제로 다시 받아야 하는 줄. 채우기와 같은 흐름으로 처리한다. */
  stale: Set<Seq>
  view: Slice
}

export class Timeline {
  #api: MaruApi
  #page: number
  #held = new Map<ChannelId, Held>()
  /** 여는 중인 채널의 첫 요청. 같은 채널을 동시에 열면 이것을 나눠 기다린다. */
  #opening = new Map<ChannelId, Promise<void>>()
  #listeners = new Set<() => void>()

  constructor(api: MaruApi, page = 50) {
    this.#api = api
    this.#page = page
  }

  subscribe = (listen: () => void): (() => void) => {
    this.#listeners.add(listen)
    return () => void this.#listeners.delete(listen)
  }

  slice = (channel: ChannelId | null): Slice => {
    if (channel === null) return EMPTY
    return this.#held.get(channel)?.view ?? EMPTY
  }

  /**
   * 채널을 연다. 이미 열었으면 아무 일도 없고, **여는 중이면 그 요청을 기다린다.** 기다리지 않으면 새로 고친
   * 직후 링크로 뛰는 `reach` 가 아직 빈 채널을 보고 곧바로 `false` 를 냈다(3차 F166).
   */
  open(channel: ChannelId): Promise<void> {
    const going = this.#opening.get(channel)
    if (going !== undefined) return going
    if (this.#held.has(channel)) return Promise.resolve()
    const load = this.#load(channel).finally(() => this.#opening.delete(channel))
    this.#opening.set(channel, load)
    return load
  }

  async #load(channel: ChannelId): Promise<void> {
    const h: Held = {
      by_seq: new Map(),
      have: 0 as Seq,
      want: 0 as Seq,
      reached_start: false,
      loading: true,
      filling: null,
      stale: new Set(),
      view: EMPTY,
    }
    this.#held.set(channel, h)
    try {
      const got = await this.#api.latest(channel, this.#page)
      for (const m of got) h.by_seq.set(m.seq, m)
      h.have = got.at(-1)?.seq ?? (0 as Seq)
      // 한 페이지가 안 차면 그게 처음이다.
      h.reached_start = got.length < this.#page
    } catch (e) {
      // 실패한 채널은 잊는다 — 다음 `open` 이 다시 시도한다. 빈 채로 두면
      // "아무 말도 없다" 가 거짓으로 보인다.
      this.#held.delete(channel)
      throw e
    }
    h.loading = false
    this.#publish(h)
    // 여는 사이 사건이 왔을 수 있다.
    if (h.want > h.have) this.#fill(channel, h)
  }

  /** 위로 스크롤. 한 페이지 더 오래된 것을 앞에 붙인다. */
  async older(channel: ChannelId): Promise<void> {
    const h = this.#held.get(channel)
    if (h === undefined || h.reached_start || h.loading) return
    const oldest = this.#sorted(h)[0]?.seq
    if (oldest === undefined) return
    h.loading = true
    this.#publish(h)
    try {
      const got = await this.#api.latest(channel, this.#page, oldest)
      for (const m of got) h.by_seq.set(m.seq, m)
      h.reached_start = got.length < this.#page
    } finally {
      h.loading = false
      this.#publish(h)
    }
  }

  /**
   * 그 줄이 화면에 있을 때까지 과거를 당긴다. 검색 결과에서 뛰어갈 때 쓴다.
   *
   * 한 번에 딱 맞는 구간을 주는 창구가 없으므로 **페이지를 거슬러 올라간다**.
   * 오래된 줄일수록 오래 걸리지만 한도(`MAX_REACH` 페이지)를 두어 멈춘다 —
   * 못 닿으면 `false` 고, 부르는 쪽이 채널만 열어 둔다.
   */
  async reach(channel: ChannelId, seq: Seq): Promise<boolean> {
    await this.open(channel)
    for (let page = 0; page < MAX_REACH; page += 1) {
      const h = this.#held.get(channel)
      if (h === undefined) return false
      if (h.by_seq.has(seq)) return true
      if (h.reached_start) return false
      const before = this.#sorted(h)[0]?.seq
      await this.older(channel)
      // 한 걸음도 못 갔으면 더 눌러도 같다 — 무한히 돌지 않는다.
      if (this.#sorted(this.#held.get(channel) as Held)[0]?.seq === before) return false
    }
    return false
  }

  /**
   * 갭 백필로 받은 줄을 그대로 넣는다. 엔진이 이미 REST 로 받은 것을 여기서
   * 또 묻지 않는다 — 같은 구간을 두 번 받는 것이 이 파일이 막으려는 바로 그 일이다.
   */
  absorb(channel: ChannelId, got: readonly Message[]): void {
    const h = this.#held.get(channel)
    if (h === undefined || got.length === 0) return
    for (const m of got) h.by_seq.set(m.seq, m)
    this.#advance(h)
    this.#publish(h)
  }

  on_event(e: Event): void {
    if (!has_channel(e)) return
    const h = this.#held.get(e.channel)
    if (h === undefined) return
    switch (e.t) {
      case 'message':
        if (e.seq > h.want) h.want = e.seq
        break
      case 'edited':
      case 'deleted':
        // 본문은 사건에 없다. 그 줄만 다시 받는다.
        if (h.by_seq.has(e.seq)) h.stale.add(e.seq)
        break
      default:
        return
    }
    if (!h.loading) this.#fill(e.channel, h)
  }

  /**
   * 끊긴 사이 무엇이 바뀌었는지 모른다 — 가진 줄 중 **최근 한 페이지**를 다시 받는다.
   *
   * 새 줄은 갭 복구가 되찾지만, 수정·삭제·반응은 번호가 늘지 않아 갭으로 드러나지
   * 않는다. 그것들은 여기서만 돌아온다. 지운 줄은 묘비로 오므로 따로 지울 것이 없다.
   * 더 오래된 줄까지 다시 받지 않는 것은 비용 때문이다 — 사람이 보고 있는 것은 끝이다.
   */
  async refresh(channel: ChannelId): Promise<void> {
    const h = this.#held.get(channel)
    if (h === undefined || h.loading || h.have === 0) return
    const oldest = Math.min(...h.by_seq.keys())
    const since = Math.max(oldest - 1, (h.have as number) - this.#page, 0) as Seq
    try {
      const got = await this.#api.message_range(channel, since, h.have)
      for (const m of got) h.by_seq.set(m.seq, m)
    } catch {
      return // 다음 재연결이 다시 부른다. 낡은 화면이 빈 화면보다 낫다.
    }
    this.#publish(h)
  }

  /** 내가 보낸 줄. 소켓 사건보다 먼저 알았으니 먼저 채운다. */
  sent(channel: ChannelId, seq: Seq): void {
    this.on_event({ t: 'message', channel, seq, author: 0 as never })
  }

  /** 잇따라 가진 줄까지 `have` 를 민다. 중간이 비면 거기서 멈춘다. */
  #advance(h: Held): void {
    while (h.by_seq.has(((h.have as number) + 1) as Seq)) h.have = ((h.have as number) + 1) as Seq
  }

  /**
   * 채널당 하나만 돈다. 도는 동안 목표가 올라가면 끝나고 다시 잇는다.
   *
   * 시작을 한 틱 미룬다 — 같은 틱에 몰려 온 사건(재접속 뒤 백필이 그렇다)이
   * 목표를 다 올린 뒤에 읽어야 요청이 하나로 합쳐진다.
   */
  #fill(channel: ChannelId, h: Held): void {
    if (h.filling !== null) return
    h.filling = Promise.resolve()
      .then(() => this.#fill_loop(channel, h))
      .finally(() => {
        h.filling = null
      })
  }

  async #fill_loop(channel: ChannelId, h: Held): Promise<void> {
    while (h.want > h.have || h.stale.size > 0) {
      const step =
        h.want > h.have ? await this.#fill_gap(channel, h) : await this.#refresh_one(channel, h)
      // 실패하면 멈춘다. 다음 사건이 다시 깨운다 — 여기서 돌면 서버가 아플 때 더 때린다.
      if (!step) break
      this.#publish(h)
    }
  }

  /** `have‥=want` 를 한 번에 받는다. 받았으면 `true`. */
  async #fill_gap(channel: ChannelId, h: Held): Promise<boolean> {
    const upto = h.want
    try {
      const got = await this.#api.message_range(channel, h.have, upto)
      if (got.length === 0) return false
      for (const m of got) h.by_seq.set(m.seq, m)
      // 서버는 `since‥=upto` 를 빠짐없이 준다(지운 줄도 묘비로 온다). 그래서 `have` 를
      // 곧장 `upto` 로 민다 — 잇따라 세면 묘비 없는 빈 자리에서 영영 멈춘다.
      h.have = upto
      return true
    } catch {
      return false
    }
  }

  /** 수정·삭제된 줄 하나를 다시 받는다. */
  async #refresh_one(channel: ChannelId, h: Held): Promise<boolean> {
    const seq = h.stale.values().next().value as Seq
    h.stale.delete(seq)
    try {
      const got = await this.#api.message_range(channel, ((seq as number) - 1) as Seq, seq)
      for (const m of got) h.by_seq.set(m.seq, m)
      return true
    } catch {
      return false
    }
  }

  #sorted(h: Held): Message[] {
    return [...h.by_seq.values()].sort((a, b) => a.seq - b.seq)
  }

  #publish(h: Held): void {
    h.view = { lines: this.#sorted(h), reached_start: h.reached_start, loading: h.loading }
    for (const l of this.#listeners) l()
  }
}
